// ─── useGuardrailsAudit.js ───────────────────────────────────────────────
// V97.27 (audit refacto) — Hook extrait de JourneyPlanEditor.jsx.
//
// Encapsule :
//   - state guardrailsState ({ guardrails, violations, completeness })
//   - ref synchrone pour eviter race avec recordPlanGeneration (HIGH-2)
//   - preload DB au mount (avec fallback hardcode silencieux)
//   - setActiveGuardrails() : appele quand le composer beta detecte des guardrails
//   - runAudit(planText) : detecte violations + completeness, met a jour state
//   - reset() : clear state (au regenerate)
//
// Le pre-existing bug HIGH-2 (race condition setState callback vs lecture
// synchrone par recordPlanGeneration) reste resolu via la ref interne.

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  auditPlanForGuardrails,
  auditPlanCompleteness,
  preloadGuardrailsFromSupabase,
} from '../services/prompts/nutrition/_clinicalGuardrails.fr';
// V97.63 : la classification des interactions est aussi préchargée ici — même
// mount que les guardrails, en amont des 4 portes de clairance (adopter, Word,
// fiche frigo, publication) qui appellent classifyInteraction.
import { preloadInteractionsFromSupabase } from '../services/clinicalInteractions';
// V97.64 : contraintes maternelles P0 préchargées au même mount (consommées
// par buildSafetyBlockFr/En à la génération de plan).
import { preloadMaternalSafetyFromSupabase, getActiveMaternalSafetyEntry } from '../services/clinicalMaternalSafety';
// V97.65 : seuils de détection préchargés au même mount (consommés par le
// détecteur de profils à la génération de plan).
import { preloadThresholdsFromSupabase } from '../services/clinicalThresholds';

/**
 * Hook audit clinique (guardrails).
 *
 * @param {{ supabase: object }} opts - Supabase client pour preload.
 * @returns {{
 *   guardrailsState: object | null,
 *   reset: () => void,
 *   setActiveGuardrails: (guardrails: Array) => void,
 *   runAudit: (planText: string) => { violations: Array, completeness: object, guardrails: Array },
 * }}
 */
export function useGuardrailsAudit({ supabase }) {
  const [guardrailsState, setGuardrailsState] = useState(null);
  // Ref pour lecture synchrone : evite race condition setState callback
  // microtask vs recordPlanGeneration synchrone (audit V97.25 HIGH-2 fix).
  const guardrailsStateRef = useRef(null);
  useEffect(() => { guardrailsStateRef.current = guardrailsState; }, [guardrailsState]);

  // Preload DB au mount. Fallback silencieux sur hardcode si DB down.
  useEffect(() => {
    if (!supabase) return;
    preloadGuardrailsFromSupabase(supabase).then((res) => {
      if (res.ok) {
        // eslint-disable-next-line no-console
        console.log(`[guardrails] preload OK from ${res.source} (${res.count} profils)`);
      } else {
        // eslint-disable-next-line no-console
        console.warn(`[guardrails] preload fallback to hardcode: ${res.error}`);
      }
    });
    preloadInteractionsFromSupabase(supabase).then((res) => {
      if (res.ok) {
        // eslint-disable-next-line no-console
        console.log(`[interactions] preload OK from ${res.source} (${res.count} substances)`);
      } else {
        // eslint-disable-next-line no-console
        console.warn(`[interactions] preload fallback to hardcode: ${res.error}`);
      }
    });
    preloadMaternalSafetyFromSupabase(supabase).then((res) => {
      if (res.ok) {
        // 2026-07-31 — le log dit desormais D'OU viennent les evictions
        // grossesse et COMBIEN il y en a. C'est le controle qu'Anissa demande
        // en session authentifiee : la RLS n'ouvre la lecture qu'au role
        // `authenticated`, donc seule une vraie session de cockpit prouve que
        // les 14 lignes arrivent de Supabase et non du repli local.
        const grossesse = getActiveMaternalSafetyEntry('grossesse', 'fr');
        const evictions = grossesse?.rules?.[4] || '';
        const nb = evictions ? evictions.split(' ; ').length : 0;
        // eslint-disable-next-line no-console
        console.log(
          `[maternal-safety] preload OK from ${res.source} (${res.count} états) · `
          + `grossesse : ${nb} évictions · source citée : `
          + `${evictions.includes('341.801.F') ? 'OSAV août 2025 (341.801.F)' : 'ABSENTE'}`,
        );
        if (res.source !== 'supabase' || nb !== 14) {
          // eslint-disable-next-line no-console
          console.warn(
            '[maternal-safety] ATTENTION : attendu 14 évictions depuis supabase, '
            + `obtenu ${nb} depuis ${res.source}.`,
          );
        }
      } else {
        // eslint-disable-next-line no-console
        console.warn(`[maternal-safety] preload fallback to hardcode: ${res.error}`);
      }
    });
    preloadThresholdsFromSupabase(supabase).then((res) => {
      if (res.ok) {
        // eslint-disable-next-line no-console
        console.log(`[thresholds] preload OK from ${res.source} (${res.count} seuils)`);
      } else {
        // eslint-disable-next-line no-console
        console.warn(`[thresholds] preload fallback to hardcode: ${res.error}`);
      }
    });
  }, [supabase]);

  const reset = useCallback(() => {
    setGuardrailsState(null);
  }, []);

  const setActiveGuardrails = useCallback((guardrails) => {
    if (Array.isArray(guardrails) && guardrails.length > 0) {
      setGuardrailsState({ guardrails, violations: [], completeness: null });
    }
  }, []);

  /**
   * Compute SYNCHRONE : retourne les valeurs calculees + update state.
   * Le caller peut utiliser le retour directement (pas besoin d'attendre
   * que React execute le setState callback).
   */
  const runAudit = useCallback((planText) => {
    const empty = { violations: [], completeness: null, guardrails: [] };
    try {
      const currentGuardrails = guardrailsStateRef.current?.guardrails || [];
      if (currentGuardrails.length === 0) return empty;
      const violations = auditPlanForGuardrails(planText, currentGuardrails);
      const completeness = auditPlanCompleteness(planText, currentGuardrails);
      setGuardrailsState((prev) => prev?.guardrails?.length
        ? { ...prev, violations, completeness }
        : prev);
      return { violations, completeness, guardrails: currentGuardrails };
    } catch (auditErr) {
      // eslint-disable-next-line no-console
      console.warn('[guardrails-audit] post-generation failed (non-bloquant):', auditErr?.message);
      return empty;
    }
  }, []);

  return {
    guardrailsState,
    reset,
    setActiveGuardrails,
    runAudit,
  };
}
